import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import { FlatList, Pressable, SxProp, View } from 'dripsy';
import { UserEarnOfferListResponseDataItem } from '@utx/utx-client';

import { EarnStackParamList, EarnStackRoutes } from '../../earn.navigator';
import { SkeletonView } from '@/design-system/components/cards';
import { UniCheckbox } from '@/design-system/components/inputs/uniCheckbox';
import { H5, TextBody, TextTitle, TextView } from '@/design-system/components/typography';
import { PopularOfferItem } from './PopularOfferItem';
import { PopularOffersGroupNative } from './PopularOffersGroupNative';
import { UniInput } from '@/design-system/components/inputs';
import { Icon } from '@/design-system/components/icons/Icon';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { BottomSheetFilterProps } from '../../screens/bottomSheetFilter/BottomSheetFilter';

export enum PopularOffersVariant {
  Web = 'web',
  Native = 'native'
}

interface PopularOffersProps {
  offers: Array<UserEarnOfferListResponseDataItem>;
  loading?: boolean;
  sx?: SxProp;
  variant?: PopularOffersVariant;
  showAssets: {
    showMyAssetsOnly: boolean;
    toggleShowMyAssetsOnly: () => void;
  };
  onSearch: (value?: string) => void;
  bottomSheetFilterProps: BottomSheetFilterProps;
}

export const PopularOffers: React.FC<PopularOffersProps> = ({
  offers,
  sx,
  loading,
  variant = PopularOffersVariant.Web,
  showAssets,
  bottomSheetFilterProps,
  onSearch
}) => {
  const navigation = useNavigation<NativeStackNavigationProp<EarnStackParamList>>();
  const { t } = useTranslation('earn');
  const isWeb = variant === PopularOffersVariant.Web;
  const availableOffers = offers.filter((offer) => offer.available);

  function onSubscribe(offerId: string) {
    navigation.navigate(EarnStackRoutes.Subscribe, {
      offerId
    });
  }

  if (loading) {
    return (
      <View sx={{ ...styles.container, ...sx }}>
        <View sx={{ ...styles.header, mb: isWeb ? '$10' : '$5', paddingX: isWeb ? 0 : '$4' }}>
          {isWeb ? (
            <H5 bold>{t('earn-invest::glossary.popular-offers')}</H5>
          ) : (
            <TextTitle bold>{t('earn-invest::glossary.popular-offers')}</TextTitle>
          )}
          {/* <UniCheckbox
            label={t('earn-invest::glossary.show-my-asset-only')}
            value={showAssets.showMyAssetsOnly}
            onValueChange={showAssets.toggleShowMyAssetsOnly}
            verticalAlignCenter
          /> */}
        </View>
        {/* Search */}
        {!isWeb ? (
          <View sx={styles.productFilterNative}>
            <UniInput
              prefixIcon="search"
              placeholder={t('earn-invest::glossary.search-by-asset')}
              wrapperSx={{ flex: 1, flexGrow: 1 }}
              sx={{ width: '100%' }}
              onValueChange={onSearch}
            />
            <Pressable
              sx={{
                ...styles.productFilterNativeIconBtn,
                borderColor: bottomSheetFilterProps.filteringNumber > 0 ? '$hyperlink' : '$borderButton'
              }}
              onPress={() => {
                // filterProps?.onOpen();
                navigation.navigate(EarnStackRoutes.BottomSheetFilter, bottomSheetFilterProps);
              }}>
              <Icon
                name="activityHistoryFilter"
                color={bottomSheetFilterProps.filteringNumber > 0 ? '$hyperlink' : undefined}
                themeColor={bottomSheetFilterProps.filteringNumber > 0 ? '$hyperlink' : undefined}
              />
              {/*  */}
              {bottomSheetFilterProps.filteringNumber > 0 && (
                <View sx={styles.productFilterIconNumber}>
                  <TextView sx={{ fontSize: 10, color: '$textDarkBackground' }}>{bottomSheetFilterProps.filteringNumber}</TextView>
                </View>
              )}
            </Pressable>
          </View>
        ) : null}
        <View sx={{ paddingX: '$4', paddingY: '$5', justifyContent: 'center', alignItems: 'flex-start', gap: '$2' }}>
          <SkeletonView height={20} />
          <SkeletonView height={20} width={200} />
          <SkeletonView height={20} width={150} />
        </View>
      </View>
    );
  }

  if (availableOffers.length === 0) {
    return (
      <View sx={{ ...styles.container, ...sx }}>
        <View sx={{ ...styles.header, mb: isWeb ? '$10' : '$5', paddingX: isWeb ? 0 : '$4' }}>
          {isWeb ? (
            <H5 bold>{t('earn-invest::glossary.popular-offers')}</H5>
          ) : (
            <TextTitle bold>{t('earn-invest::glossary.popular-offers')}</TextTitle>
          )}
          {/* <UniCheckbox
            label={t('earn-invest::glossary.show-my-asset-only')}
            value={showAssets.showMyAssetsOnly}
            onValueChange={showAssets.toggleShowMyAssetsOnly}
            verticalAlignCenter
          /> */}
        </View>
        {/* Search */}
        {!isWeb ? (
          <View sx={styles.productFilterNative}>
            <UniInput
              prefixIcon="search"
              placeholder={t('earn-invest::glossary.search-by-asset')}
              wrapperSx={{ flexGrow: 1 }}
              sx={{ width: '100%' }}
              onValueChange={onSearch}
            />
            <Pressable
              sx={{
                ...styles.productFilterNativeIconBtn,
                borderColor: bottomSheetFilterProps.filteringNumber > 0 ? '$hyperlink' : '$borderButton'
              }}
              onPress={() => {
                // filterProps?.onOpen()
                navigation.navigate(EarnStackRoutes.BottomSheetFilter, bottomSheetFilterProps);
              }}>
              <Icon
                name="activityHistoryFilter"
                color={bottomSheetFilterProps.filteringNumber > 0 ? '$hyperlink' : undefined}
                themeColor={bottomSheetFilterProps.filteringNumber > 0 ? '$hyperlink' : undefined}
              />
              {/*  */}
              {bottomSheetFilterProps.filteringNumber > 0 && (
                <View sx={styles.productFilterIconNumber}>
                  <TextView sx={{ fontSize: 10, color: '$textDarkBackground' }}>{bottomSheetFilterProps.filteringNumber}</TextView>
                </View>
              )}
            </Pressable>
          </View>
        ) : null}
        <View sx={{ paddingY: '$5', justifyContent: 'center', alignItems: 'center', gap: '$2' }}>
          <Icon name="listZeroState" height={60} width={60} />
          <TextBody bold label>
            {t('earn-invest::glossary.no-offers')}
          </TextBody>
        </View>
      </View>
    );
  }

  return (
    <View sx={{ ...styles.container, ...sx }}>
      {/* Header */}
      <View sx={{ ...styles.header, mb: isWeb ? '$10' : '$5', paddingX: isWeb ? 0 : '$4' }}>
        {isWeb ? (
          <H5 bold>{t('earn-invest::glossary.popular-offers')}</H5>
        ) : (
          <TextTitle bold>{t('earn-invest::glossary.popular-offers')}</TextTitle>
        )}
        {/* <UniCheckbox
          label={t('earn-invest::glossary.show-my-asset-only')}
          value={showAssets.showMyAssetsOnly}
          onValueChange={showAssets.toggleShowMyAssetsOnly}
          verticalAlignCenter
        /> */}
      </View>
      {/* Search */}
      {!isWeb ? (
        <View sx={styles.productFilterNative}>
          <UniInput
            prefixIcon="search"
            placeholder={t('earn-invest::glossary.search-by-asset')}
            wrapperSx={{ flexGrow: 1 }}
            sx={{ width: '100%' }}
            onValueChange={onSearch}
          />
          <Pressable
            sx={{
              ...styles.productFilterNativeIconBtn,
              borderColor: bottomSheetFilterProps.filteringNumber > 0 ? '$hyperlink' : '$borderButton'
            }}
            onPress={() => {
              // filterProps?.onOpen()
              navigation.navigate(EarnStackRoutes.BottomSheetFilter, bottomSheetFilterProps);
            }}>
            <Icon
              name="activityHistoryFilter"
              color={bottomSheetFilterProps.filteringNumber > 0 ? '$hyperlink' : undefined}
              themeColor={bottomSheetFilterProps.filteringNumber > 0 ? '$hyperlink' : undefined}
            />
            {/*  */}
            {bottomSheetFilterProps.filteringNumber > 0 && (
              <View sx={styles.productFilterIconNumber}>
                <TextView sx={{ fontSize: 10, color: '$textDarkBackground' }}>{bottomSheetFilterProps.filteringNumber}</TextView>
              </View>
            )}
          </Pressable>
        </View>
      ) : null}
      {/* Horizontal Scroll */}
      {isWeb ? (
        <FlatList
          sx={styles.horizontalScrollContainer}
          horizontal={true}
          data={availableOffers}
          renderItem={({ item }) => (
            <PopularOfferItem
              offer={item}
              onPress={() => onSubscribe(item.id)}
              sx={{ flex: 'auto', width: 333, mb: 0 }}
              variant={variant}
            />
          )}
        />
      ) : (
        <PopularOffersGroupNative offers={availableOffers} onSubscribe={onSubscribe} />
      )}
    </View>
  );
};

enum StyleName {
  container = 'container',
  header = 'header',
  horizontalScrollContainer = 'horizontalScrollContainer',
  verticalScrollContainer = 'verticalScrollContainer',
  productFilterNative = 'productFilterNative',
  productFilterNativeIconBtn = 'productFilterNativeIconBtn',
  productFilterIconNumber = 'productFilterIconNumber'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    flex: 1
  },
  header: {
    mb: '$10',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  horizontalScrollContainer: {
    padding: '$1',
    flexDirection: 'row'
  },
  verticalScrollContainer: {
    paddingY: '$4',
    flexDirection: 'column',
    gap: '$3'
  },
  productFilterNative: { flexDirection: 'row', gap: '$3', marginX: '$4', mt: '$3', mb: '$7' },
  productFilterNativeIconBtn: {
    height: '$11',
    width: '$11',
    borderWidth: 1,
    borderRadius: 5,
    // borderColor: '$borderButton',
    justifyContent: 'center',
    alignItems: 'center'
  },
  productFilterIconNumber: {
    position: 'absolute',
    top: 2,
    right: 2,
    width: '$4',
    height: '$4',
    borderWidth: 1,
    borderRadius: 100,
    borderColor: '$hyperlink',
    backgroundColor: '$hyperlink',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 100
  }
};
